import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { CommentCountBadge } from '@ContractBuilder/components/CommentCountBadge';
import { CircleIconWrapper } from '@ContractBuilder/contract-builder-header/components/CircleIconWrapper';
import { ViewPath } from '@ContractBuilder/contract-builder-header/constants';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { useEntityStore } from '@ContractBuilder/store';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { isSubmission } from '@helpers/getDocumentMode';
import { ChatAltIcon, ClockIcon } from '@heroicons/react/outline';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import clsx from 'clsx';
import { get } from 'lodash-es';

export const ShowCommentsAndActivitySection = () => {
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const { closeDrawer, isDrawerOpen, openDrawer, state } = useDrawerStore(
    ({ closeDrawer, isOpen, openDrawer, state }) => ({
      closeDrawer,
      isDrawerOpen: isOpen,
      openDrawer,
      state,
    }),
  );
  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { pathname } = useLocation();
  const getFeatureFlagState = useFeatureFlags();

  const isCommentsEnabled = getFeatureFlagState('Comments') && isSubmission(pathname);

  const isActivityDrawerOpen = isDrawerOpen && state?.type === DrawerLayoutComponent.Activity;
  const isCommentsDrawerOpen = isDrawerOpen && state?.type === DrawerLayoutComponent.Comments;

  const isEditing = editingBlockId !== undefined;

  // Calculate unresolved comments count
  const comments = get(submission, 'comments', []);
  const unresolvedCommentsCount = comments.filter((comment) => !comment.is_resolved).length;

  const handleCommentsDrawerToggleClick = () => {
    if (isDrawerOpen && isCommentsDrawerOpen) {
      return closeDrawer();
    }

    return openDrawer({ type: DrawerLayoutComponent.Comments, props: {} });
  };

  useEffect(() => {
    const currentPath = pathname.split('/').pop() as ViewPath;
    if (![ViewPath.Contract, ViewPath.Endorsements].includes(currentPath)) {
      closeDrawer();
    }
    // eslint-disable-next-line -- We only want to run this hook when switching between contract and form views
  }, [pathname]);

  const handleActivityDrawerToggleClick = () => {
    if (isActivityDrawerOpen) {
      return closeDrawer();
    }

    return openDrawer({ type: DrawerLayoutComponent.Activity, props: {} });
  };

  if (!submission) {
    return null;
  }

  return (
    <div className="flex gap-x-3">
      <Show when={isCommentsEnabled}>
        <CommentCountBadge count={unresolvedCommentsCount}>
          <Tooltip content={isCommentsDrawerOpen ? 'Hide comments' : 'Show comments'} disabled={isEditing}>
            <CircleIconWrapper
              data-testid="comments-toggle-button"
              icon={ChatAltIcon}
              isActive={isCommentsDrawerOpen}
              onClick={handleCommentsDrawerToggleClick}
              wrapperClassName={clsx(isEditing && 'pointer-events-none bg-transparent opacity-50')}
            />
          </Tooltip>
        </CommentCountBadge>
      </Show>
      <Tooltip content={`${isActivityDrawerOpen ? 'Hide' : 'Show'} activity`} disabled={isEditing}>
        <CircleIconWrapper
          data-testid="activity-log-button"
          icon={ClockIcon}
          isActive={isActivityDrawerOpen}
          onClick={handleActivityDrawerToggleClick}
          wrapperClassName={clsx(isEditing && 'pointer-events-none opacity-50')}
        />
      </Tooltip>
    </div>
  );
};
